import React, { useCallback, useEffect, useState } from "react";
import {
    Table,
    Input,
    Select,
    InputNumber,
    Space,
    Button,
    Modal,
    Form,
    message,
    Popconfirm,
    Tag,
    Card,
    Avatar,
    Tooltip,
    Drawer,
    Descriptions,
    Image,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    UserOutlined,
    CheckCircleFilled,
    WarningFilled,
    MailOutlined,
    PhoneOutlined,
    IdcardOutlined,
    DeleteOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import CopyText from "../components/CopyText";
import PointsTier from "../components/PointsTier";

const ROLE_OPTIONS = [
    { value: "VOLUNTEER", label: "VOLUNTEER" },
    { value: "GROUP_ADMIN", label: "GROUP_ADMIN" },
    { value: "SUPER_ADMIN", label: "SUPER_ADMIN" },
];

const roleColor = (r) =>
    r === "SUPER_ADMIN" ? "red" : r === "GROUP_ADMIN" ? "orange" : "blue";

const VerifyIcon = ({ ok, okText, badText, icon }) => (
    <Tooltip title={ok ? okText : badText}>
        {ok ? (
            <CheckCircleFilled style={{ color: "#16a34a" }} />
        ) : (
            <WarningFilled style={{ color: "#f59e0b" }} />
        )}
        <span style={{ display: "none" }}>{icon}</span>
    </Tooltip>
);

const UsersPage = () => {
    const [data, setData] = useState({ items: [], total: 0 });
    const [groups, setGroups] = useState([]);
    const [groupMap, setGroupMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [sort, setSort] = useState({ field: "createdAt", order: "desc" });
    const [filters, setFilters] = useState({
        q: "",
        role: undefined,
        gender: undefined,
        ageMin: undefined,
        ageMax: undefined,
        groupId: undefined,
    });
    const [selectedKeys, setSelectedKeys] = useState([]);

    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null);
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listUsers({
                ...filters,
                page,
                limit,
                sortField: sort.field,
                sortOrder: sort.order,
            });
            setData(res);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [filters, page, limit, sort]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        api.listGroups()
            .then((r) => {
                setGroups(r.items);
                const map = {};
                r.items.forEach((g) => (map[String(g._id)] = g.name));
                setGroupMap(map);
            })
            .catch(() => {});
    }, []);

    const openEdit = (user) => {
        setEditing(user);
        form.setFieldsValue({
            name: user.name,
            email: user.email,
            phone: user.phone,
            age: user.age,
            gender: user.gender,
            role: user.role,
            points: user.points,
            coins: user.coins,
            hours: user.hours,
            avatar: user.avatar,
        });
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
    };

    const save = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            if (editing && editing._id) {
                await api.updateUser(editing._id, values);
                message.success("User updated");
            } else {
                await api.createUser(values);
                message.success("User created");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteUser(id);
            message.success("User deleted");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const bulkDelete = async () => {
        try {
            await Promise.all(selectedKeys.map((id) => api.deleteUser(id)));
            message.success(`${selectedKeys.length} users deleted`);
            setSelectedKeys([]);
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const sortOrderFor = (field) =>
        sort.field === field
            ? sort.order === "asc"
                ? "ascend"
                : "descend"
            : null;

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (_, u) => (
                <Space>
                    <Avatar size="small" src={u.avatar} icon={<UserOutlined />} />
                    <span>{u.name || "—"}</span>
                    <Space size={3}>
                        <VerifyIcon
                            ok={u.isMailVerified}
                            okText="Email verified"
                            badText="Email not verified"
                        />
                        <Tooltip title={u.isPhoneVerified ? "Phone verified" : "Phone not verified"}>
                            <PhoneOutlined
                                style={{
                                    fontSize: 12,
                                    color: u.isPhoneVerified ? "#16a34a" : "#cbd5e1",
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={u.isProfileComplete ? "Profile complete" : "Profile incomplete"}>
                            <IdcardOutlined
                                style={{
                                    fontSize: 12,
                                    color: u.isProfileComplete ? "#16a34a" : "#cbd5e1",
                                }}
                            />
                        </Tooltip>
                    </Space>
                </Space>
            ),
        },
        { title: "Email", dataIndex: "email", key: "email" },
        {
            title: "Group",
            dataIndex: "groupId",
            key: "groupId",
            render: (gid) => (gid ? groupMap[String(gid)] || "—" : "—"),
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            render: (r, u) => (
                <Space size={4} wrap>
                    <Tag color={roleColor(r)} style={{ marginInlineEnd: 0 }}>
                        {r}
                    </Tag>
                    {r === "GROUP_ADMIN" && u.groupId && (
                        <span className="admin-cell-sub">
                            of {groupMap[String(u.groupId)] || "—"}
                        </span>
                    )}
                </Space>
            ),
        },
        { title: "Age", dataIndex: "age", key: "age", width: 80, sorter: true, sortOrder: sortOrderFor("age") },
        {
            title: "Points",
            dataIndex: "points",
            key: "points",
            width: 110,
            sorter: true,
            sortOrder: sortOrderFor("points"),
            render: (p) => <PointsTier points={p} />,
        },
        {
            title: "Hours",
            dataIndex: "hours",
            key: "hours",
            width: 90,
            sorter: true,
            sortOrder: sortOrderFor("hours"),
            render: (h) => Math.round(Number(h) || 0),
        },
        {
            title: "Events",
            dataIndex: "attendedEventsCount",
            key: "attendedEventsCount",
            width: 90,
            sorter: true,
            sortOrder: sortOrderFor("attendedEventsCount"),
        },
        {
            title: "Actions",
            key: "actions",
            width: 150,
            render: (_, row) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(row)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this user?"
                        onConfirm={() => remove(row._id)}
                    >
                        <Button size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const setF = (patch) => {
        setPage(1);
        setFilters((f) => ({ ...f, ...patch }));
    };

    const onTableChange = (pagination, _filters, sorter) => {
        if (pagination.current) setPage(pagination.current);
        if (sorter && sorter.field) {
            setSort({
                field: sorter.order ? sorter.field : "createdAt",
                order: sorter.order === "ascend" ? "asc" : "desc",
            });
        }
    };

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Users</h1>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    {selectedKeys.length > 0 && (
                        <Popconfirm
                            title={`Delete ${selectedKeys.length} selected users?`}
                            onConfirm={bulkDelete}
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Delete ({selectedKeys.length})
                            </Button>
                        </Popconfirm>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        New user
                    </Button>
                </Space>
            </div>

            <Card variant="borderless" style={{ marginBottom: 16 }}>
                <Space wrap>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Search name / email / phone"
                        style={{ width: 260 }}
                        onChange={(e) => setF({ q: e.target.value })}
                    />
                    <Select allowClear placeholder="Role" style={{ width: 150 }} options={ROLE_OPTIONS} onChange={(v) => setF({ role: v })} />
                    <Select
                        allowClear
                        placeholder="Gender"
                        style={{ width: 120 }}
                        options={[
                            { value: "male", label: "male" },
                            { value: "female", label: "female" },
                        ]}
                        onChange={(v) => setF({ gender: v })}
                    />
                    <InputNumber placeholder="Age min" min={0} onChange={(v) => setF({ ageMin: v })} />
                    <InputNumber placeholder="Age max" min={0} onChange={(v) => setF({ ageMax: v })} />
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Search by group"
                        style={{ width: 200 }}
                        options={groups.map((g) => ({ value: g._id, label: g.name }))}
                        onChange={(v) => setF({ groupId: v })}
                    />
                </Space>
            </Card>

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={data.items}
                scroll={{ x: "max-content" }}
                rowSelection={{
                    selectedRowKeys: selectedKeys,
                    onChange: setSelectedKeys,
                }}
                onRow={(record) => ({
                    className: "admin-row-clickable",
                    onClick: (e) => {
                        if (
                            e.target.closest(".ant-table-selection-column") ||
                            e.target.closest("button") ||
                            e.target.closest(".ant-checkbox-wrapper") ||
                            e.target.closest(".ant-popover")
                        )
                            return;
                        setDetail(record);
                    },
                })}
                onChange={onTableChange}
                pagination={{
                    current: page,
                    pageSize: limit,
                    total: data.total,
                    showTotal: (t) => `${t} users`,
                }}
            />

            {/* Detail drawer */}
            <Drawer
                open={!!detail}
                onClose={() => setDetail(null)}
                width={440}
                title={detail ? detail.name || detail.email : ""}
                extra={
                    detail && (
                        <Button
                            type="primary"
                            onClick={() => {
                                openEdit(detail);
                                setDetail(null);
                            }}
                        >
                            Edit
                        </Button>
                    )
                }
            >
                {detail && (
                    <>
                        <div style={{ textAlign: "center", marginBottom: 16 }}>
                            {detail.avatar ? (
                                <Image
                                    src={detail.avatar}
                                    alt={detail.name}
                                    width={96}
                                    height={96}
                                    style={{ borderRadius: "50%", objectFit: "cover" }}
                                    preview={{ mask: "Zoom" }}
                                />
                            ) : (
                                <Avatar size={96} icon={<UserOutlined />} />
                            )}
                        </div>
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Email">
                                <CopyText value={detail.email}>
                                    <span>
                                        <MailOutlined /> {detail.email || "—"}
                                    </span>
                                </CopyText>
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">
                                <CopyText value={detail.phone}>
                                    <span>
                                        <PhoneOutlined /> {detail.phone || "—"}
                                    </span>
                                </CopyText>
                            </Descriptions.Item>
                            <Descriptions.Item label="Role">
                                <Tag color={roleColor(detail.role)}>{detail.role}</Tag>
                                {detail.role === "GROUP_ADMIN" && detail.groupId && (
                                    <span className="admin-cell-sub">
                                        {" "}admin of {groupMap[String(detail.groupId)] || "—"}
                                    </span>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Group">
                                {detail.groupId ? groupMap[String(detail.groupId)] || "—" : "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Age">{detail.age ?? "—"}</Descriptions.Item>
                            <Descriptions.Item label="Gender">{detail.gender || "—"}</Descriptions.Item>
                            <Descriptions.Item label="Points / Rank">
                                <PointsTier points={detail.points} showLabel />
                            </Descriptions.Item>
                            <Descriptions.Item label="Hours">{Math.round(Number(detail.hours) || 0)}</Descriptions.Item>
                            <Descriptions.Item label="Attended events">{detail.attendedEventsCount ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Languages">
                                {(detail.languages || []).length
                                    ? detail.languages.map((l) => <Tag key={l}>{l}</Tag>)
                                    : "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label="OTP attempts">{detail.otpAttempts ?? 0}</Descriptions.Item>
                            <Descriptions.Item label="Created">{fmtDate(detail.createdAt, true)}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Drawer>

            {/* Create / edit modal */}
            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit user" : "New user"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input />
                    </Form.Item>
                    {!(editing && editing._id) && (
                        <Form.Item name="password" label="Password">
                            <Input.Password />
                        </Form.Item>
                    )}
                    <Space style={{ display: "flex" }} align="start">
                        <Form.Item name="phone" label="Phone">
                            <Input />
                        </Form.Item>
                        <Form.Item name="age" label="Age">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="gender" label="Gender">
                            <Select
                                allowClear
                                style={{ width: 120 }}
                                options={[
                                    { value: "male", label: "male" },
                                    { value: "female", label: "female" },
                                ]}
                            />
                        </Form.Item>
                    </Space>
                    <Form.Item name="role" label="Role" rules={[{ required: true }]}>
                        <Select options={ROLE_OPTIONS} />
                    </Form.Item>
                    <Space style={{ display: "flex" }} align="start">
                        <Form.Item name="points" label="Points">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item
                            name="coins"
                            label="CTRL coins"
                            tooltip="Gift-shop balance the user can spend on gifts"
                        >
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="hours" label="Hours">
                            <InputNumber min={0} />
                        </Form.Item>
                    </Space>
                    <Form.Item name="avatar" label="Avatar URL">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UsersPage;
